import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import {
    CreateGroup,
    CreateGroupMember,
    GroupListResponse,
    GroupMember,
    GroupMemberListResponse,
    GroupMemberQueryParams,
    GroupQueryParams,
    IGroup,
    UpdateGroup,
    UpdateGroupMember
} from '@frontend/group/types';

export class QuestionnaireClient extends APIClient {
    public static readonly endpoint = '/group-api/v1';

    public static async fetchQuestionnaires(queryParams?: ApiQueryParams<GroupQueryParams>): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, GroupQueryParams>(`${this.endpoint}/questionnaires`, queryParams, undefined);
    }

    public static async resolveQuestionnaires(ids: string[], field: string = 'id'): Promise<GroupListResponse> {
        return this.resolve<GroupListResponse>(`${this.endpoint}/questionnaires`, ids, field);
    }

    public static async deleteQuestionnaires(groupIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/questionnaires-delete`, { ids: groupIds });
        return await this.handleVoidResponse(response);
    }

    public static async postQuestionnaire(accountId: string, group: CreateGroup): Promise<IGroup> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/questionnaires`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async fetchQuestionnaire(accountId: string, groupId: string): Promise<IGroup> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}`);
        return await this.handleResponse<IGroup>(response);
    }

    public static async patchQuestionnaire(accountId: string, groupId: string, group: UpdateGroup): Promise<IGroup> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async deleteQuestionnaire(accountId: string, groupId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupsNotInGroup(accountId: string, groupId: string): Promise<GroupListResponse> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/groups`);
        return await this.handleResponse<GroupListResponse>(response);
    }

    //IGroup members
    public static async fetchQuestionnaireMembers(queryParams?: ApiQueryParams<GroupMemberQueryParams>): Promise<GroupMemberListResponse> {
        return await this.apiPaginated<GroupMemberListResponse, GroupMemberQueryParams>(`${this.endpoint}/questionnaire-members`, queryParams);
    }

    public static async resolveQuestionnaireMembers(ids: string[], field: string = 'id'): Promise<GroupMemberListResponse> {
        return this.resolve<GroupMemberListResponse>(`${this.endpoint}/questionnaire-members`, ids, field);
    }

    public static async deleteQuestionnaireMembers(memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/questionnaire-members-delete`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async postQuestionnaireMember(accountId: string, groupId: string, groupMember: CreateGroupMember): Promise<GroupMember> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/members`, groupMember);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async postQuestionnaireMemberList(accountId: string, groupId: string, entity_type: EntityType, memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/${entity_type}s/members-list`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async fetchQuestionnaireMember(accountId: string, groupId: string, memberId: string): Promise<GroupMember> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/members/${memberId}`);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async patchQuestionnaireMember(accountId: string, groupId: string, memberId: string, body: UpdateGroupMember): Promise<GroupMember> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/members/${memberId}`, body);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async deleteQuestionnaireMember(accountId: string, groupId: string, memberId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/questionnaires/${groupId}/members/${memberId}`);
        return await this.handleVoidResponse(response);
    }
}
